// src/components/Implement/ImplementComponent.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, ListGroup } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';
import outputs from '../../../amplify_outputs.json';

type Issue = Schema['Issue']['type'];
type Profile = Schema['Profile']['type'];

/** ============ External endpoint from outputs (already the full enqueue URL) ============ */
const PROPOSE_STRATEGIES_ENQUEUE = String(
  (outputs as any)?.custom?.proposeStrategiesApiUrl || ''
).trim();

/** ============ Tiny utils ============ */
function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = maybeWrapped as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}
function isNonEmptyString(x: unknown): x is string {
  return typeof x === 'string' && x.trim().length > 0;
}
function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}
const safe = (s?: string | null) => (typeof s === 'string' ? s.trim() : '');
const lower = (s?: string | null) => safe(s).toLowerCase();

function isInitiator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && lower(me) === lower(issue.initiatorEmail);
}
function isCollaborator(me: string | null | undefined, issue: Issue | null): boolean {
  return !!me && !!issue && lower(me) === lower(issue.collaboratorEmail);
}

/** ============ Neutral Solution helpers ============ */
type NeutralSolution = { title: string; summary?: string; steps?: string[] };

function parseBestSolutionJson(x: unknown): any | null {
  if (!x) return null;
  if (typeof x === 'object') return x as any;
  if (typeof x === 'string') {
    try { return JSON.parse(x); } catch { return null; }
  }
  return null;
}
function cleanSummary(summary: string | undefined): string | undefined {
  if (!summary) return summary;
  return summary.replace(/\s*\([^)]*\)/g, '').replace(/\s+\./g, '.').trim();
}
function getNeutralSolutionFromIssue(issue: any): NeutralSolution | null {
  const raw = parseBestSolutionJson(issue?.bestSolutionJson);
  if (!raw) return null;

  const chosenId: string | undefined = raw.chosenId ?? raw.normalized?.id;
  const proposals: any[] = Array.isArray(raw.proposals) ? raw.proposals : [];
  const fromChosen = chosenId ? proposals.find((p) => p?.id === chosenId) : null;

  const src = fromChosen ?? raw.normalized ?? raw.raw ?? null;
  if (!src || typeof src !== 'object') return null;

  const title =
    typeof src.title === 'string' && src.title.trim() ? src.title.trim() : 'Proposed Solution';
  const summary =
    typeof src.summary === 'string' && src.summary.trim() ? cleanSummary(src.summary) : undefined;
  const steps = Array.isArray(src.steps)
    ? src.steps.filter((s: any) => typeof s === 'string' && s.trim())
    : undefined;

  return { title, summary, steps };
}

export default function ImplementComponent() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { email: userEmail, firstName } = useUserContext();

  const sessionId = useMemo(() => decodeURIComponent(id ?? '').trim().replace(/\/+$/, ''), [id]);

  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [error, setError] = useState<string | null>(null);

  // enqueue guard
  const enqueuedRef = useRef(false);
  const mounted = useRef(true);

  /** Track latest issue for polling logic */
  const latestIssueRef = useRef<Issue | null>(null);
  useEffect(() => { latestIssueRef.current = issue; }, [issue]);

  /** ✅ Only log bestSolutionJson once per mount after first successful fetch */
  const loggedBestOnceRef = useRef(false);

  /** ============ Initial read (one-shot) ============ */
  async function readIssue(logRaw = true) {
    const raw = (await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' })) as unknown;
    const data = unwrap<Issue>(raw);
    if (mounted.current) setIssue(data);
    if (logRaw) console.log('[Implement] Issue row from DB:', data);

    if (!loggedBestOnceRef.current) {
      const bestSolutionJson = (data as any)?.bestSolutionJson ?? null;
      console.log('[Implement] Issue.bestSolutionJson (initial from DB):', bestSolutionJson);
      if (typeof bestSolutionJson === 'string') {
        try {
          console.log('[Implement] Issue.bestSolutionJson (parsed):', JSON.parse(bestSolutionJson));
        } catch (e) {
          console.warn('[Implement] Issue.bestSolutionJson parse error:', e);
        }
      }
      loggedBestOnceRef.current = true;
    }
    return data;
  }

  useEffect(() => {
    mounted.current = true;
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try { await readIssue(true); }
      catch (e: any) { setError(e?.message ?? 'Failed to load session.'); }
      finally { if (mounted.current) setLoading(false); }
    })();
    return () => { mounted.current = false; };
  }, [sessionId]);

  /** ============ observeQuery subscription ============ */
  useEffect(() => {
    if (!sessionId) return;
    const observable = (client.models.Issue as any).observeQuery?.({
      filter: { id: { eq: sessionId } },
      authMode: 'userPool',
    });

    if (!observable?.subscribe) return;

    const sub = observable.subscribe({
      next: (snapshot: any) => {
        const items: Issue[] = Array.isArray(snapshot?.items) ? snapshot.items : [];
        const row = items.find((x) => x.id === sessionId) ?? items[0] ?? null;
        if (row) {
          setIssue(row);
          console.debug('[Implement] observeQuery tick. counts:', {
            iStrats: (row as any)?.initiatorStrategies?.length || 0,
            cStrats: (row as any)?.collaboratorStrategies?.length || 0,
            iTips:   (row as any)?.initiatorCommunicationTips?.length || 0,
            cTips:   (row as any)?.collaboratorCommunicationTips?.length || 0,
            iEmo:    (row as any)?.initiatorEmotionalTips?.length || 0,
            cEmo:    (row as any)?.collaboratorEmotionalTips?.length || 0,
          });
        }
      },
      error: (e: any) => console.warn('[Issue.observeQuery] error', e),
    });

    return () => sub.unsubscribe();
  }, [sessionId]);

  /** ============ Role ============ */
  const meIsInitiator = useMemo(() => isInitiator(userEmail, issue), [userEmail, issue]);
  const meIsCollaborator = useMemo(() => isCollaborator(userEmail, issue), [userEmail, issue]);
  const roleKnown = meIsInitiator || meIsCollaborator;
  const roleLabel = meIsInitiator ? 'initiator' : meIsCollaborator ? 'collaborator' : 'unknown';

  useEffect(() => {
    if (!sessionId || !roleKnown) return;
    (async () => {
      try {
        await client.models.Issue.update({ id: sessionId, status: 'implemented' as any }, { authMode: 'userPool' });
      } catch { /* no-op */ }
    })();
  }, [sessionId, roleKnown]);

  /** ============ Per-role readiness derivations ============ */
  const iStrats = coerceStrings((issue as any)?.initiatorStrategies as any);
  const cStrats = coerceStrings((issue as any)?.collaboratorStrategies as any);
  const iTips   = coerceStrings((issue as any)?.initiatorCommunicationTips as any);
  const cTips   = coerceStrings((issue as any)?.collaboratorCommunicationTips as any);
  const iEmo    = coerceStrings((issue as any)?.initiatorEmotionalTips as any);
  const cEmo    = coerceStrings((issue as any)?.collaboratorEmotionalTips as any);

  const myStrats = meIsInitiator ? iStrats : cStrats;
  const myTips   = meIsInitiator ? iTips   : cTips;
  const myEmo    = meIsInitiator ? iEmo    : cEmo;

  const partnerStrats = meIsInitiator ? cStrats : iStrats;
  const partnerTips   = meIsInitiator ? cTips   : iTips;
  const partnerEmo    = meIsInitiator ? cEmo    : iEmo;

  const haveAnyForMe = (myStrats.length > 0) || (myTips.length > 0) || (myEmo.length > 0);
  const partnerReady = (partnerStrats.length > 0) || (partnerTips.length > 0) || (partnerEmo.length > 0);
  const bothReady = haveAnyForMe && partnerReady;

  /** ============ Profile subscription + enqueue ============ */
  const otherEmail = useMemo(() => {
    if (!issue) return '';
    return meIsInitiator ? safe(issue.collaboratorEmail) : meIsCollaborator ? safe(issue.initiatorEmail) : '';
  }, [issue, meIsInitiator, meIsCollaborator]);

  useEffect(() => {
    if (!otherEmail || !sessionId) return;
    const emailLower = lower(otherEmail);
    if (!emailLower) return;

    const observable = (client.models.Profile as any).observeQuery?.({
      filter: { emailLower: { eq: emailLower } },
      authMode: 'userPool',
    });
    if (!observable?.subscribe) return;

    const sub = observable.subscribe({
      next: async (snapshot: any) => {
        const items: Profile[] = Array.isArray(snapshot?.items) ? snapshot.items : [];
        const row = items[0];
        const otherDcs = safe(row?.dcsMode);

        if (otherDcs && !bothReady && PROPOSE_STRATEGIES_ENQUEUE && !enqueuedRef.current && issue?.id) {
          try {
            enqueuedRef.current = true;
            const res = await fetch(PROPOSE_STRATEGIES_ENQUEUE, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ issueId: issue.id }),
            });
            if (!res.ok) {
              enqueuedRef.current = false;
              console.warn('[Implement] enqueue failed status:', res.status, await res.text());
            } else {
              console.log('[Implement] ProposeStrategies enqueued after other DCS appeared for', issue.id);
            }
          } catch (e) {
            enqueuedRef.current = false;
            console.warn('[Implement] enqueue error:', e);
          }
        }
      },
      error: (e: any) => console.warn('[Profile.observeQuery] error', e),
    });

    return () => sub.unsubscribe();
  }, [otherEmail, sessionId, bothReady, issue?.id]);

  /** ============ Polling fallback to surface strategies reliably ============ */
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 30;
    const BASE_MS = 2500;
    const MULTIPLIER = 1.25;
    const MAX_MS = 15000;

    function hasBoth(i: Issue | null) {
      if (!i) return false;
      const iS = Array.isArray((i as any)?.initiatorStrategies) ? (i as any).initiatorStrategies : [];
      const cS = Array.isArray((i as any)?.collaboratorStrategies) ? (i as any).collaboratorStrategies : [];
      const iT = Array.isArray((i as any)?.initiatorCommunicationTips) ? (i as any).initiatorCommunicationTips : [];
      const cT = Array.isArray((i as any)?.collaboratorCommunicationTips) ? (i as any).collaboratorCommunicationTips : [];
      const iE = Array.isArray((i as any)?.initiatorEmotionalTips) ? (i as any).initiatorEmotionalTips : [];
      const cE = Array.isArray((i as any)?.collaboratorEmotionalTips) ? (i as any).collaboratorEmotionalTips : [];
      return (iS.length + iT.length + iE.length > 0) && (cS.length + cT.length + cE.length > 0);
    }

    async function tick() {
      if (cancelled) return;

      if (hasBoth(latestIssueRef.current)) return;

      attempt += 1;
      try {
        const data = await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' }) as unknown;
        const next = unwrap<Issue>(data);
        if (next) {
          setIssue(next);
          latestIssueRef.current = next;

          const iS = (next as any)?.initiatorStrategies?.length || 0;
          const cS = (next as any)?.collaboratorStrategies?.length || 0;
          const iT = (next as any)?.initiatorCommunicationTips?.length || 0;
          const cT = (next as any)?.collaboratorCommunicationTips?.length || 0;
          const iE = (next as any)?.initiatorEmotionalTips?.length || 0;
          const cE = (next as any)?.collaboratorEmotionalTips?.length || 0;

          console.debug('[Implement] poll attempt', attempt, { iS, cS, iT, cT, iE, cE });

          if (hasBoth(next)) return;
        }
      } catch (e) {
        console.warn('[Implement] poll error:', e);
      }

      if (!cancelled && attempt < MAX_ATTEMPTS) {
        const delay = Math.min(BASE_MS * Math.pow(MULTIPLIER, attempt - 1), MAX_MS);
        setTimeout(tick, delay);
      }
    }

    const initialHasBoth = hasBoth(latestIssueRef.current);
    if (!initialHasBoth) setTimeout(tick, BASE_MS);

    const forceTimer = setTimeout(() => {
      if (!cancelled) {
        console.debug('[Implement] force refresh at 30s');
        readIssue(false).catch(() => {});
      }
    }, 30000);

    function onVis() {
      if (document.visibilityState === 'visible') {
        console.debug('[Implement] visibility refresh');
        readIssue(false).catch(() => {});
      }
    }
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearTimeout(forceTimer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [sessionId]);

  /** ============ Derived UI ============ */
  const initiatorFirst = (issue?.initiatorFirstName ?? '').trim();
  const collaboratorFirst = (issue?.collaboratorFirstName ?? '').trim();
  const otherPartyFirst = meIsInitiator ? collaboratorFirst : meIsCollaborator ? initiatorFirst : '';

  const neutralSolution = useMemo(() => getNeutralSolutionFromIssue(issue), [issue]);

  /** ============ Render ============ */
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading…</div>
      </Container>
    );
  }
  if (error || !issue) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error ?? 'Session not found.'}</Alert>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate(`/session/${encodeURIComponent(sessionId)}`)}>
            Back to Session
          </Button>
          <Button variant="secondary" onClick={() => navigate('/session')}>My Sessions</Button>
        </div>
      </Container>
    );
  }
  if (!userEmail || !roleKnown) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Please sign in as the invited participant to continue.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="mx-auto" style={{ maxWidth: 900 }}>
        <Card.Header as="h5">Implement the Solution</Card.Header>
        <Card.Body>
          {/* Greeting + relationship context */}
          <div className="mb-3">
            <div className="fw-semibold">Hi{firstName ? `, ${firstName}` : ''}! 👋</div>
            {otherPartyFirst && (
              <div className="text-muted small">
                You're the <strong>{roleLabel}</strong> and you're working with <strong>{otherPartyFirst}</strong>.
              </div>
            )}
            {/* Agreed Issue display */}
            {isNonEmptyString((issue as any)?.agreedIssue) && (
              <div className="mt-2 text-muted small">
                <span className="fw-semibold">Agreed Issue: </span>
                {(issue as any).agreedIssue}
              </div>
            )}

            {/* Neutral Solution */}
            {neutralSolution && (
              <Card className="mt-3 mb-1" bg="light" text="dark">
                <Card.Header className="fw-semibold">Best Solution</Card.Header>
                <Card.Body>
                  <h6 className="mb-1">{neutralSolution.title}</h6>
                  {neutralSolution.summary && <p className="text-muted mb-2">{neutralSolution.summary}</p>}
                  {Array.isArray(neutralSolution.steps) && neutralSolution.steps.length > 0 && (
                    <>
                      <div className="small text-muted mb-2">Suggested steps</div>
                      <ListGroup className="mb-2">
                        {neutralSolution.steps.map((s, i) => (
                          <ListGroup.Item key={`neutral-step-main-${i}`}>{s}</ListGroup.Item>
                        ))}
                      </ListGroup>
                    </>
                  )}
                  <div className="small text-muted">
                    This shared plan is meant to feel fair to both sides. Try it for a short trial and adjust together.
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Strategies/tips */}
          {haveAnyForMe ? (
            <div className="mb-3">
              <h6 className="mb-2">Ways to implement the solution</h6>
              <div className="row">
                {/* My panel */}
                <div className="col-md-6">
                  <Card className="mb-3" bg="light" text="dark">
                    <Card.Header className="fw-semibold">{meIsInitiator ? 'Initiator' : 'Collaborator'}</Card.Header>
                    <Card.Body>
                      {myStrats.length > 0 && (
                        <ListGroup className="mb-2">
                          {myStrats.map((s, i) => (
                            <ListGroup.Item key={`me-strat-${i}`}>{s}</ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                      {myTips.length > 0 && (
                        <>
                          <div className="small text-muted mb-1">Communication Tips</div>
                          <ListGroup className="mb-2">
                            {myTips.map((s, i) => (
                              <ListGroup.Item key={`me-tip-${i}`}>{s}</ListGroup.Item>
                            ))}
                          </ListGroup>
                        </>
                      )}
                      {myEmo.length > 0 && (
                        <>
                          <div className="small text-muted mb-1">Emotional Navigation</div>
                          <ListGroup>
                            {myEmo.slice(0, 2).map((s, i) => (
                              <ListGroup.Item key={`me-emo-${i}`}>{s}</ListGroup.Item>
                            ))}
                          </ListGroup>
                        </>
                      )}
                      {myStrats.length === 0 && myTips.length === 0 && myEmo.length === 0 && (
                        <div className="text-muted small">No items yet.</div>
                      )}
                    </Card.Body>
                  </Card>
                </div>

                {/* Partner panel */}
                <div className="col-md-6">
                  <Card className="mb-3" bg="light" text="dark">
                    <Card.Header className="fw-semibold">{meIsInitiator ? 'Collaborator' : 'Initiator'}</Card.Header>
                    <Card.Body>
                      {partnerReady ? (
                        <>
                          {partnerStrats.length > 0 && (
                            <ListGroup className="mb-2">
                              {partnerStrats.map((s, i) => (
                                <ListGroup.Item key={`partner-strat-${i}`}>{s}</ListGroup.Item>
                              ))}
                            </ListGroup>
                          )}
                          {partnerTips.length > 0 && (
                            <>
                              <div className="small text-muted mb-1">Communication Tips</div>
                              <ListGroup className="mb-2">
                                {partnerTips.map((s, i) => (
                                  <ListGroup.Item key={`partner-tip-${i}`}>{s}</ListGroup.Item>
                                ))}
                              </ListGroup>
                            </>
                          )}
                          {partnerEmo.length > 0 && (
                            <>
                              <div className="small text-muted mb-1">Emotional Navigation</div>
                              <ListGroup>
                                {partnerEmo.slice(0, 2).map((s, i) => (
                                  <ListGroup.Item key={`partner-emo-${i}`}>{s}</ListGroup.Item>
                                ))}
                              </ListGroup>
                            </>
                          )}
                          {partnerStrats.length === 0 && partnerTips.length === 0 && partnerEmo.length === 0 && (
                            <div className="text-muted small">No items yet.</div>
                          )}
                        </>
                      ) : (
                        <Alert variant="info" className="mb-0">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Waiting for the other side’s items…
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="info" className="mb-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Preparing your strategies and tips…
            </Alert>
          )}

          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => window.print()}>
              Print / Save PDF
            </Button>
            <Button variant="secondary" onClick={() => navigate('/session')}>
              My Sessions
            </Button>
            <Button variant="primary" onClick={() => navigate(`/init`)}>
              Start New Session
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
